import { useEffect, useState } from 'react';
import { useCompany } from '../../context/CompanyContext';
import ImportModal from '../../components/ImportModal';
import api from '../../api';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EMPTY_FORM = {
  total_sales: '', igst_sales: '', cgst_sales: '', sgst_sales: '',
  total_itc: '', igst_itc: '', cgst_itc: '', sgst_itc: '',
};

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Rounds a stored float to 2 decimals before it goes into a form field -
// Tally-sync figures can arrive as e.g. 41369.549999999996 (float summation
// noise), which otherwise shows up raw and ugly in a number input.
function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function GstEntryTab() {
  const { selectedCompanyId } = useCompany();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [existingRecord, setExistingRecord] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const loadData = () => {
    if (!selectedCompanyId) return;
    api
      .get(`/gst/${selectedCompanyId}`, { params: { month, year } })
      .then((res) => {
        const existing = res.data.exists ? res.data.data : null;
        setExistingRecord(existing);
        setForm(
          existing
            ? {
                total_sales: round2(existing.total_sales), igst_sales: round2(existing.igst_sales),
                cgst_sales: round2(existing.cgst_sales), sgst_sales: round2(existing.sgst_sales),
                total_itc: round2(existing.total_itc), igst_itc: round2(existing.igst_itc),
                cgst_itc: round2(existing.cgst_itc), sgst_itc: round2(existing.sgst_itc),
              }
            : EMPTY_FORM
        );
        setSaved(null);
      })
      .catch((err) => setError(err.message));
  };

  const handleDelete = () => {
    if (!existingRecord) return;
    setDeleting(true);
    api
      .delete(`/gst-data/${existingRecord.id}`)
      .then(() => {
        setDeleteConfirmOpen(false);
        loadData();
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setDeleting(false));
  };

  useEffect(loadData, [selectedCompanyId, month, year]);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      company_id: selectedCompanyId,
      month,
      year,
      ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v) || 0])),
    };
    api
      .post('/gst-data', payload)
      .then((res) => setSaved(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <form className="dash-card" onSubmit={handleSave}>
      <div className="page-title-row" style={{ marginBottom: 0 }}>
        <h2 className="dash-card-title">GST Data Entry</h2>
        <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
          Import
        </button>
      </div>

      <div className="de-period-row">
        <div className="field-group">
          <span className="field-label">Month</span>
          <select className="field-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>
        <div className="field-group">
          <span className="field-label">Year</span>
          <input
            className="field-input"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </div>
      </div>

      {error && <p className="dash-error">{error}</p>}

      <div className="field-row">
        <div className="field-group">
          <span className="field-label">Total Sales (taxable) ₹</span>
          <input className="field-input" type="number" value={form.total_sales} onChange={setField('total_sales')} />
        </div>
        <div className="field-group">
          <span className="field-label">IGST on Sales ₹</span>
          <input className="field-input" type="number" value={form.igst_sales} onChange={setField('igst_sales')} />
        </div>
        <div className="field-group">
          <span className="field-label">CGST on Sales ₹</span>
          <input className="field-input" type="number" value={form.cgst_sales} onChange={setField('cgst_sales')} />
        </div>
        <div className="field-group">
          <span className="field-label">SGST on Sales ₹</span>
          <input className="field-input" type="number" value={form.sgst_sales} onChange={setField('sgst_sales')} />
        </div>
        <div className="field-group">
          <span className="field-label">Total ITC Available ₹</span>
          <input className="field-input" type="number" value={form.total_itc} onChange={setField('total_itc')} />
        </div>
        <div className="field-group">
          <span className="field-label">IGST ITC ₹</span>
          <input className="field-input" type="number" value={form.igst_itc} onChange={setField('igst_itc')} />
        </div>
        <div className="field-group">
          <span className="field-label">CGST ITC ₹</span>
          <input className="field-input" type="number" value={form.cgst_itc} onChange={setField('cgst_itc')} />
        </div>
        <div className="field-group">
          <span className="field-label">SGST ITC ₹</span>
          <input className="field-input" type="number" value={form.sgst_itc} onChange={setField('sgst_itc')} />
        </div>
      </div>

      <div className="dash-btn-row">
        <button className="dash-btn dash-btn-orange" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save GST Data'}
        </button>
        {existingRecord && (
          <button
            className="dash-btn-red-outline"
            type="button"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete This Month's Data
          </button>
        )}
      </div>

      {saved && (
        <div className="de-summary">
          Saved — Net GST Payable for {MONTH_NAMES[month - 1]} {year}: {inr(saved.net_payable)}
        </div>
      )}

      {deleteConfirmOpen && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteConfirmOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete GST Data</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Delete the saved GST data for <strong>{MONTH_NAMES[month - 1]} {year}</strong>? This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteConfirmOpen(false)}>
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-orange"
                style={{ background: '#dc2626' }}
                type="button"
                disabled={deleting}
                onClick={handleDelete}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="gst_data"
        moduleLabel="GST Data Entry"
        companyId={selectedCompanyId}
        onImported={loadData}
      />
    </form>
  );
}

export default GstEntryTab;
